import { faGears } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { toast } from 'react-toastify';
import { ResultSerivces } from 'Services/resultService';
import { ExamServices } from 'Services/examServices';
import { parse } from 'json2csv';

interface IMarksModal {
    show: any,
    onHide: any,
    examId: any
}

type ResultMode = 'whole' | 'section';

interface ISectionMarking {
    sectionId: string,
    sectionName: string,
    marksForCorrectOption: string,
    marksForIncorrectOption: string,
    marksWhenIsDeletedTrue: string,
}

const MarksModal = ({ show, onHide, examId }: IMarksModal) => {

    const [loading, setLoading] = useState<boolean>(false);
    const [mode, setMode] = useState<ResultMode>('whole');

    const [marking, setMarking] = useState<any>({
        marksForCorrectOption: "",
        marksWhenIsDeletedTrue: "",
        marksForIncorrectOption: ""
    });

    const [sectionMarking, setSectionMarking] = useState<ISectionMarking[]>([]);
    const [sectionsLoaded, setSectionsLoaded] = useState<boolean>(false);

    const downloadCsv = (rows: any[], fileName: string) => {
        if (!rows || rows.length === 0) return;
        const blob = new Blob([parse(rows)], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleMarking = (e: any) => {
        setMarking({ ...marking, [e.target.name]: e.target.value });
    }

    const handleSectionMarking = (index: number, field: keyof ISectionMarking, value: string) => {
        setSectionMarking((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    }

    const loadSections = async () => {
        try {
            const res = await ExamServices.getExamSections(examId);
            const sections = res?.data?.sections || res?.data?.data?.sections || [];
            setSectionMarking(sections.map((s: any) => ({
                sectionId: s._id,
                sectionName: s.name || "Section",
                marksForCorrectOption: "",
                marksForIncorrectOption: "",
                marksWhenIsDeletedTrue: "",
            })));
            setSectionsLoaded(true);
        } catch (err) {
            toast.error("Unable to load sections");
        }
    }

    useEffect(() => {
        if (mode === 'section' && !sectionsLoaded) {
            loadSections();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    // Whole-exam result: one CSV for the entire examination
    const generateWholeResult = async () => {
        setLoading(true);
        await toast.promise(
            async () => {
                const res = await ResultSerivces.generateResult(examId, marking);
                if (res.status === 200) {
                    const rows = Array.isArray(res.data) ? res.data : (res.data?.global || []);
                    if (!rows.length) {
                        throw new Error("No results to download");
                    }
                    downloadCsv(rows, 'Exam_Results.csv');
                } else {
                    throw new Error("Unable to generate results");
                }
            },
            {
                pending: "Generating Results",
                success: "Results Generated",
                error: "Unable to generate results"
            }
        );
        setLoading(false);
    };

    // Section-wise result: an overall CSV plus one CSV per section
    const generateSectionResult = async () => {
        // Every section must have its marks filled in
        const incomplete = sectionMarking.some((s) =>
            s.marksForCorrectOption === "" ||
            s.marksForIncorrectOption === "" ||
            s.marksWhenIsDeletedTrue === ""
        );
        if (sectionMarking.length === 0) {
            toast.error("No sections found for this exam");
            return;
        }
        if (incomplete) {
            toast.error("Please enter marks for every section");
            return;
        }

        setLoading(true);
        await toast.promise(
            async () => {
                const payload = {
                    sections: sectionMarking.map((s) => ({
                        sectionId: s.sectionId,
                        sectionName: s.sectionName,
                        marksForCorrectOption: Number(s.marksForCorrectOption),
                        marksForIncorrectOption: Number(s.marksForIncorrectOption),
                        marksWhenIsDeletedTrue: Number(s.marksWhenIsDeletedTrue),
                    }))
                };
                const res = await ResultSerivces.generateResultSectionWise(examId, payload);
                if (res.status === 200) {
                    const global = res.data?.global || [];
                    const sectionWise = res.data?.sectionWise || {};
                    downloadCsv(global, 'Overall_Results.csv');
                    Object.keys(sectionWise).forEach((name) => {
                        downloadCsv(sectionWise[name], `${name}_Results.csv`);
                    });
                } else {
                    throw new Error("Unable to generate results");
                }
            },
            {
                pending: "Generating Section-wise Results",
                success: "Section-wise Results Generated",
                error: "Unable to generate results"
            }
        );
        setLoading(false);
    };

    const generateResult = () => {
        if (mode === 'section') {
            generateSectionResult();
        } else {
            generateWholeResult();
        }
    };

    return (
        <>
            <Modal
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                show={show}
            >
                <Modal.Header>
                    <Modal.Title id="contained-modal-title-vcenter">
                        Generate Result
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Result Type</Form.Label>
                            <div className="d-flex gap-4">
                                <Form.Check
                                    type="radio"
                                    id="mode-whole"
                                    label="Whole Examination"
                                    name="resultMode"
                                    checked={mode === 'whole'}
                                    onChange={() => setMode('whole')}
                                />
                                <Form.Check
                                    type="radio"
                                    id="mode-section"
                                    label="Section Wise"
                                    name="resultMode"
                                    className="ms-3"
                                    checked={mode === 'section'}
                                    onChange={() => setMode('section')}
                                />
                            </div>
                        </Form.Group>

                        <hr />

                        {mode === 'whole' ? (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>Marks for correct Option</Form.Label>
                                    <Form.Control type="number" name="marksForCorrectOption" onChange={handleMarking} required />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Marks for Incorrect Option</Form.Label>
                                    <Form.Control type="number" name="marksForIncorrectOption" onChange={handleMarking} required />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Marks for deleted Question</Form.Label>
                                    <Form.Control type="number" name="marksWhenIsDeletedTrue" onChange={handleMarking} required />
                                </Form.Group>
                            </>
                        ) : (
                            <>
                                <p className="text-muted mb-3">
                                    Enter the marking for each section. Each section can have its own marks
                                    for correct, incorrect and deleted questions.
                                </p>
                                {sectionMarking.length === 0 ? (
                                    <p>No sections found for this exam.</p>
                                ) : (
                                    sectionMarking.map((section, index) => (
                                        <div key={section.sectionId} className="border rounded p-3 mb-3">
                                            <h6 className="mb-3">{section.sectionName}</h6>
                                            <div className="d-flex gap-3 flex-wrap">
                                                <Form.Group>
                                                    <Form.Label>Marks for correct Option</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        value={section.marksForCorrectOption}
                                                        onChange={(e) => handleSectionMarking(index, 'marksForCorrectOption', e.target.value)}
                                                        required
                                                    />
                                                </Form.Group>
                                                <Form.Group>
                                                    <Form.Label>Marks for Incorrect Option</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        value={section.marksForIncorrectOption}
                                                        onChange={(e) => handleSectionMarking(index, 'marksForIncorrectOption', e.target.value)}
                                                        required
                                                    />
                                                </Form.Group>
                                                <Form.Group>
                                                    <Form.Label>Marks for deleted Question</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        value={section.marksWhenIsDeletedTrue}
                                                        onChange={(e) => handleSectionMarking(index, 'marksWhenIsDeletedTrue', e.target.value)}
                                                        required
                                                    />
                                                </Form.Group>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </>
                        )}

                        <div className="d-flex align-item-center">
                            <Button variant="primary" type="button" className="d-flex float-right me-3" onClick={generateResult} disabled={loading}>
                                <FontAwesomeIcon icon={faGears} className="me-1" />  Generate
                            </Button>
                            <Button variant="danger" type="button" className="ms-2" onClick={onHide}>
                                Cancel
                            </Button>
                        </div>
                    </>
                </Modal.Body>
            </Modal>
        </>
    )
}

export default MarksModal
